import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiPlus, FiEye, FiMoreVertical, FiEdit2, FiTrash2, FiCopy, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { PLAYLIST_LIST, PLAYLIST_DELETE } from '../../api/contracts';
import './Page.css';
import './Category.css';

const COL_COUNT = 8;
const MIN_WIDTHS_PX = [100, 90, 70, 70, 100, 100, 120, 80];
const INITIAL_WIDTHS_PCT = [14, 10, 8, 8, 14, 14, 18, 14];
const PAGE_SIZE = 50;

function truncateUrl(url, max = 24) {
  if (!url || typeof url !== 'string') return '—';
  return url.length > max ? url.slice(0, max) + '…' : url;
}

function Playlist() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [columnWidths, setColumnWidths] = useState(INITIAL_WIDTHS_PCT);
  const [resizeState, setResizeState] = useState({ index: null, startX: 0, startWidths: [] });
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const tableRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return playlists;
    const q = search.trim().toLowerCase();
    return playlists.filter(
      (p) => (p.id && p.id.toLowerCase().includes(q)) || (p.title && p.title.toLowerCase().includes(q))
    );
  }, [playlists, search]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIndex, startIndex + PAGE_SIZE);
  const displayStart = totalItems === 0 ? 0 : startIndex + 1;
  const displayEnd = totalItems === 0 ? 0 : Math.min(startIndex + PAGE_SIZE, totalItems);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages, totalItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useLayoutEffect(() => {
    if (!actionMenuId || !triggerRef.current) {
      setMenuPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [actionMenuId]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (actionMenuId && !triggerRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setActionMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuId]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(PLAYLIST_LIST.path), { method: PLAYLIST_LIST.method, headers })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        const list = data.data || data;
        setPlaylists(Array.isArray(list) ? list : []);
        setListError(Array.isArray(list) ? '' : 'Invalid response from server.');
      })
      .catch((err) => {
        if (!cancelled) setListError(err.message || 'Failed to load categories.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleEdit = (row) => {
    setActionMenuId(null);
    navigate(`/dashboard/cms/playlist/${row.id}/edit`);
  };

  const handleDuplicate = (row) => {
    setActionMenuId(null);
    navigate('/dashboard/cms/playlist/create', { state: { duplicateId: row.id } });
  };

  const handleRemove = async (row) => {
    setActionMenuId(null);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(apiUrl(PLAYLIST_DELETE.pathFor(row.id)), {
        method: PLAYLIST_DELETE.method,
        headers,
      });
      if (res.ok) {
        setPlaylists((prev) => prev.filter((p) => p.id !== row.id));
      }
    } catch {
      // keep list as is
    }
  };

  const handleResizeStart = (handleIndex, e) => {
    e.preventDefault();
    setResizeState({ index: handleIndex, startX: e.clientX, startWidths: [...columnWidths] });
  };

  useEffect(() => {
    if (resizeState.index === null) return;
    const table = tableRef.current;
    if (!table) return;
    const onMove = (e) => {
      const tw = table.offsetWidth;
      if (tw <= 0) return;
      const i = resizeState.index;
      const minL = (MIN_WIDTHS_PX[i] / tw) * 100;
      const minR = (MIN_WIDTHS_PX[i + 1] / tw) * 100;
      const total = resizeState.startWidths[i] + resizeState.startWidths[i + 1];
      const delta = ((e.clientX - resizeState.startX) / tw) * 100;
      let newL = resizeState.startWidths[i] + delta;
      newL = Math.max(minL, Math.min(total - minR, newL));
      setColumnWidths((prev) => {
        const next = [...prev];
        next[i] = newL;
        next[i + 1] = total - newL;
        return next;
      });
    };
    const onUp = () => setResizeState((s) => ({ ...s, index: null }));
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [resizeState.index, resizeState.startX, resizeState.startWidths]);

  const headers = ['Title', 'Status', 'Popularity', 'Rank', 'Small thumb', 'Thumbnail URL', 'Thumb landscape', 'Action'];

  return (
    <div className="page category-page">
      <div className="category-header">
        <h3>Categories</h3>
        <p className="category-subtitle">Manage categories</p>
      </div>

      <div className="category-toolbar">
        <div className="search-wrap">
          <FiSearch className="search-icon" aria-hidden />
          <input
            type="text"
            className="search-input"
            placeholder="Search by ID or title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search categories"
          />
        </div>
        <Link to="/dashboard/cms/playlist/create" className="btn-create">
          <FiPlus className="btn-icon" aria-hidden />
          Create category
        </Link>
      </div>

      {loading && <p className="create-category-loading">Loading categories…</p>}
      {listError && <p className="create-category-error" role="alert">{listError}</p>}

      <div className="table-wrap">
        <table ref={tableRef} className="category-table category-table-resizable">
          <colgroup>
            {columnWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}%`, minWidth: MIN_WIDTHS_PX[i] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {headers.map((label, i) => (
                <th key={i} className={i === COL_COUNT - 1 ? 'th-action' : ''} style={{ width: `${columnWidths[i]}%` }}>
                  <span className="th-inner">{label}</span>
                  {i < COL_COUNT - 1 && (
                    <span className="resize-handle" onMouseDown={(e) => handleResizeStart(i, e)} role="separator" aria-orientation="vertical" aria-label={`Resize column ${i + 1}`} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} className="table-empty">No categories found.</td>
              </tr>
            ) : (
              paginated.map((row) => (
                <tr key={row.id}>
                  <td className="cell-name">{row.title}</td>
                  <td>
                    <span className={`status-pill status-${(row.status || '').toLowerCase()}`}>{row.status || '—'}</span>
                  </td>
                  <td className="cell-number">{row.popularity_score ?? '—'}</td>
                  <td className="cell-number">{row.rank_score ?? '—'}</td>
                  <td>
                    <div className="cell-thumbnail">
                      <span className="thumbnail-url" title={row.small_thumbnail}>{truncateUrl(row.small_thumbnail)}</span>
                      <button type="button" className="btn-watch" onClick={() => setPreviewUrl(row.small_thumbnail)} title="View" aria-label="View small thumbnail">
                        <FiEye className="watch-icon" />
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="cell-thumbnail">
                      <span className="thumbnail-url" title={row.thumbnail_url}>{truncateUrl(row.thumbnail_url)}</span>
                      <button type="button" className="btn-watch" onClick={() => setPreviewUrl(row.thumbnail_url)} title="View" aria-label="View thumbnail">
                        <FiEye className="watch-icon" />
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="cell-thumbnail">
                      <span className="thumbnail-url" title={row.thumbnail_url_landscape}>{truncateUrl(row.thumbnail_url_landscape, 28)}</span>
                      <button type="button" className="btn-watch" onClick={() => setPreviewUrl(row.thumbnail_url_landscape)} title="View" aria-label="View landscape thumbnail">
                        <FiEye className="watch-icon" />
                      </button>
                    </div>
                  </td>
                  <td className="cell-action">
                    <div className="action-wrap">
                      <button
                        ref={actionMenuId === row.id ? triggerRef : null}
                        type="button"
                        className="btn-dots"
                        onClick={() => setActionMenuId(actionMenuId === row.id ? null : row.id)}
                        aria-label="Open menu"
                        aria-expanded={actionMenuId === row.id}
                      >
                        <FiMoreVertical className="dots-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="pagination-wrap">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FiChevronLeft className="pagination-arrow" aria-hidden />
          </button>
          <span className="pagination-text">
            Showing {displayStart}–{displayEnd} of {totalItems}
          </span>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
          >
            <FiChevronRight className="pagination-arrow" aria-hidden />
          </button>
        </div>
      )}

      {actionMenuId && menuPosition && (() => {
        const row = filtered.find((p) => p.id === actionMenuId);
        if (!row) return null;
        return createPortal(
          <div ref={menuRef} className="action-menu action-menu-portal" style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, zIndex: 1000 }}>
            <button type="button" className="action-menu-item" onClick={() => handleEdit(row)}>
              <FiEdit2 className="action-menu-icon" />
              Edit category
            </button>
            <button type="button" className="action-menu-item" onClick={() => handleDuplicate(row)}>
              <FiCopy className="action-menu-icon" />
              Duplicate category
            </button>
            <button
              type="button"
              className="action-menu-item"
              disabled={row.status !== 'INACTIVE'}
              onClick={() => row.status === 'INACTIVE' && handleRemove(row)}
              title={row.status !== 'INACTIVE' ? 'Only inactive categories can be removed' : 'Remove category'}
            >
              <FiTrash2 className="action-menu-icon" />
              Remove category
            </button>
          </div>,
          document.body
        );
      })()}

      {previewUrl && (
        <div className="thumbnail-overlay" onClick={() => setPreviewUrl(null)} role="dialog" aria-modal="true" aria-label="Thumbnail preview">
          <div className="thumbnail-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn-close-preview" onClick={() => setPreviewUrl(null)} aria-label="Close preview">
              <FiX className="close-icon" />
            </button>
            <img src={previewUrl} alt="Thumbnail" className="thumbnail-preview-img" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Playlist;
